// src/components/UserForm.js
import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { EditIcon, PlusIcon, AlertTriangleIcon } from './Icon';

// Fallback if the roles endpoint isn't reachable yet (e.g. pre-deploy).
const FALLBACK_ROLES = [
  { name: 'admin', label: 'Administrator' },
  { name: 'manager', label: 'Manager' },
  { name: 'viewer', label: 'Viewer' },
  { name: 'salesperson', label: 'Salesperson' },
];

export default function UserForm({ user, onClose, onSaved }) {
  const isEdit = !!user;

  const [roles, setRoles] = useState(FALLBACK_ROLES);
  const [projects, setProjects] = useState([]);

  // Projects this user (salesperson) is assigned to for Sales Audit.
  const [assignedProjects, setAssignedProjects] = useState(
    () => new Set((Array.isArray(user?.projects) ? user.projects : []).map(Number))
  );

  // Load the roles defined in Roles & Access so the dropdown stays in sync.
  useEffect(() => {
    api.roles().then(res => {
      if (res && Array.isArray(res.roles) && res.roles.length) setRoles(res.roles);
    }).catch(() => {});
    api.projects().then(res => {
      if (res && Array.isArray(res.projects)) setProjects(res.projects);
    }).catch(() => {});
  }, []);

  const toggleProject = (id) => setAssignedProjects(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const [form, setForm] = useState({
    id:        user?.id || '',
    username:  user?.username || '',
    name:      user?.name || '',
    mobile:    user?.mobile || '',
    role:      user?.role || 'admin',
    is_active: user ? (user.is_active === 1 || user.is_active === '1' ? 1 : 0) : 1,
    password:  '',
  });

  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const update = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrMsg('');

    const username = form.username.trim();
    if (!username) { setErrMsg('Username is required'); return; }
    if (!/^[A-Za-z0-9._-]{3,60}$/.test(username)) {
      setErrMsg('Username must be 3–60 chars: letters, digits, dot, underscore or hyphen');
      return;
    }
    const mobile = form.mobile.replace(/\D/g, '');
    if (mobile && !/^[6-9]\d{9}$/.test(mobile)) {
      setErrMsg('Mobile must be a valid 10-digit number');
      return;
    }

    // Password is optional now (a user can log in via mobile OTP instead),
    // but if one is entered it must meet the length requirement.
    if (form.password && form.password.length < 6) {
      setErrMsg('Password must be at least 6 characters');
      return;
    }
    // A brand-new user needs at least one way to sign in.
    if (!isEdit && !form.password && !mobile) {
      setErrMsg('Set a password or a mobile number so the user can log in');
      return;
    }

    setSaving(true);
    const payload = {
      id:                  form.id || undefined,
      username,
      name:                form.name.trim(),
      mobile,
      role:                form.role,
      is_active:           form.is_active ? 1 : 0,
      projects:            Array.from(assignedProjects),
    };
    if (form.password) payload.password = form.password;

    try {
      const res = await api.userSave(payload);
      if (res?.success) onSaved();
      else setErrMsg(res?.error || 'Save failed');
    } catch (err) {
      setErrMsg('Network error: ' + err.message);
    }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {isEdit ? <EditIcon size={18} /> : <PlusIcon size={18} />}
            <span>{isEdit ? 'Edit User' : 'Add New User'}</span>
          </h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <Field label="Username *">
            <input className="modal-input" value={form.username} onChange={e => update('username', e.target.value)} required placeholder="e.g. sonam.admin" autoComplete="off" />
          </Field>

          <Field label="Full Name">
            <input className="modal-input" value={form.name} onChange={e => update('name', e.target.value)} placeholder="e.g. Sonam Sharma" />
          </Field>

          <Field label="Mobile Number" hint="Used for OTP login. 10-digit Indian number.">
            <input className="modal-input" type="tel" inputMode="numeric" maxLength={10}
              value={form.mobile}
              onChange={e => update('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))}
              placeholder="e.g. 9876543210" autoComplete="off" />
          </Field>

          <div className="modal-row2">
            <Field label="Role *">
              <select className="modal-input" value={form.role} onChange={e => update('role', e.target.value)}>
                {roles.map(r => <option key={r.name} value={r.name}>{r.label || (r.name.charAt(0).toUpperCase() + r.name.slice(1))}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select className="modal-input" value={form.is_active} onChange={e => update('is_active', +e.target.value)}>
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </Field>
          </div>

          <Field
            label={isEdit ? 'New Password' : 'Password'}
            hint={isEdit ? 'Leave blank to keep the current password.' : 'Optional — leave blank for OTP-only login. Min 6 characters if set.'}
          >
            <input type="password" className="modal-input" value={form.password} onChange={e => update('password', e.target.value)} placeholder={isEdit ? '••••••' : 'Optional'} autoComplete="new-password" />
          </Field>

          <fieldset className="role-perm-group" style={{ marginTop: 4 }}>
            <legend className="role-perm-legend">Assigned Projects (Sales Audit)</legend>
            {projects.length === 0 ? (
              <div className="modal-hint">No projects yet. Create a project first.</div>
            ) : (
              <div className="role-perm-list">
                {projects.map(p => (
                  <label key={p.id} className="role-perm-item">
                    <input
                      type="checkbox"
                      checked={assignedProjects.has(Number(p.id))}
                      onChange={() => toggleProject(Number(p.id))}
                    />
                    <span>{p.code_name}{p.location ? ` — ${p.location}` : ''}</span>
                  </label>
                ))}
              </div>
            )}
            <div className="modal-hint">This salesperson will see the selected projects (and their visitors) in the Sales Audit app.</div>
          </fieldset>

          {errMsg && (
            <div className="modal-err">
              <AlertTriangleIcon size={15} /> {errMsg}
            </div>
          )}

          <div className="modal-footer">
            <button type="button" className="modal-cancel" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="modal-save" disabled={saving}>
              {saving ? 'Saving...' : (isEdit ? 'Update User' : 'Create User')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="modal-field">
      <label className="modal-label">{label}</label>
      {children}
      {hint && <div className="modal-hint">{hint}</div>}
    </div>
  );
}
